import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { login, loginAsGuest } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/lobby';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError('Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGuestPlay = async () => {
    setIsLoading(true);
    try {
      await loginAsGuest();
      navigate(from, { replace: true });
    } catch (err) {
      setError('Failed to create guest session');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-shark-800 rounded-xl p-8 shadow-xl">
          <h2 className="text-3xl font-bold text-center mb-8">Welcome Back</h2>

          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-200 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-shark-700 border border-shark-600 rounded-lg focus:ring-2 focus:ring-shark-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-shark-700 border border-shark-600 rounded-lg focus:ring-2 focus:ring-shark-500 focus:border-transparent"
                required
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-shark-500 hover:bg-shark-600 disabled:bg-shark-700 text-white py-3 rounded-lg font-semibold transition"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-shark-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-shark-800 text-gray-400">Or</span>
              </div>
            </div>

            <button
              onClick={handleGuestPlay}
              disabled={isLoading}
              className="mt-4 w-full bg-gray-600 hover:bg-gray-700 disabled:bg-gray-800 text-white py-3 rounded-lg font-semibold transition"
            >
              Play as Guest
            </button>
          </div>

          <p className="mt-6 text-center text-gray-400">
            Don't have an account?{' '}
            <Link to="/register" className="text-shark-400 hover:underline">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
